/**
 * Current user profile – avatar, name, bio, stats, edit, lift history (delete, show in feed, progress chart).
 */
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { subscribeProfile, updateProfile, uploadProfileImage } from '../../domain/usecases/profile';
import { subscribeUserWorkouts, deleteWorkout, setUserFeedWorkout, resetWorkoutVerifications } from '../../domain/usecases/workouts';
import { NATIONALITIES } from '../../data/nationalities';
import { SearchableSelect } from '../../shared/ui/SearchableSelect';
import { DISCIPLINES, DISCIPLINE_LABELS } from '../../domain/entities/User';
import { WeightChart } from '../../shared/ui/WeightChart';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import Button from '../../shared/ui/Button.jsx';
import Display from '../../shared/ui/Display.jsx';
import Title from '../../shared/ui/Title.jsx';
import Body from '../../shared/ui/Body.jsx';
import Label from '../../shared/ui/Label.jsx';
import Input from '../../shared/ui/Input.jsx';
import Textarea from '../../shared/ui/Textarea.jsx';
import NumericInput from '../../shared/ui/NumericInput.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import { canAskPushPermission, currentPushPermission, enablePushForUser } from '../../shared/utils/pushNotifications';
import styles from './ProfilePage.module.css';

export function ProfilePage() {
  const { userId, profile: initialProfile } = useAuth();
  const [profile, setProfile] = useState(initialProfile);
  const [workouts, setWorkouts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    displayName: '', bio: '', bodyweight: '', federation: '', nationality: '', age: '', discipline: '',
  });
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [settingFeedId, setSettingFeedId] = useState(null);
  const [chartExercise, setChartExercise] = useState('');
  const [videoModal, setVideoModal] = useState(null); // { videoUrl, exercise, weight, reps }
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    const unsub = subscribeProfile(userId, setProfile);
    return () => unsub();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const unsub = subscribeUserWorkouts(userId, 500, setWorkouts);
    return () => unsub();
  }, [userId]);

  useEffect(() => {
    if (profile) {
      setForm({
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        bodyweight: profile.bodyweight ?? '',
        federation: profile.federation || '',
        nationality: profile.nationality || '',
        age: profile.age ?? '',
        discipline: profile.discipline || '',
      });
    }
  }, [profile]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateProfile(userId, {
        displayName: form.displayName || undefined,
        bio: form.bio || undefined,
        bodyweight: form.bodyweight ? Number(form.bodyweight) : null,
        federation: form.federation || undefined,
        nationality: form.nationality || undefined,
        age: form.age ? parseInt(form.age, 10) : null,
        discipline: form.discipline || undefined,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setAvatarUploading(true);
    try {
      const url = await uploadProfileImage(userId, file);
      setProfile((p) => (p ? { ...p, avatarUrl: url } : p));
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  }

  async function handleDelete(workoutId) {
    if (!userId) return;
    setDeletingId(workoutId);
    try {
      await deleteWorkout(workoutId, userId);
    } finally {
      setDeletingId(null);
    }
  }

  async function handleShowInFeed(workoutId) {
    if (!userId) return;
    setSettingFeedId(workoutId);
    try {
      await setUserFeedWorkout(userId, workoutId);
      await resetWorkoutVerifications(workoutId);
    } finally {
      setSettingFeedId(null);
    }
  }

  const exerciseOptions = [...new Set(workouts.map((w) => w.exercise).filter(Boolean))].sort();
  const chartData = chartExercise
    ? workouts
        .filter((w) => w.exercise === chartExercise)
        .sort((a, b) => new Date((a.createdAt && a.createdAt.toDate?.()) || a.createdAt || 0) - new Date((b.createdAt && b.createdAt.toDate?.()) || b.createdAt || 0))
        .map((w) => ({
          date: (w.createdAt && w.createdAt.toDate?.()) ? w.createdAt.toDate().toISOString() : (w.createdAt || ''),
          weight: Number(w.weight) || 0,
        }))
    : [];

  if (!profile) {
    return (
      <div className={styles.page}>
        <Skeleton width="100%" height={160} radius="var(--radius-md)" style={{ marginBottom: '1rem' }} />
        <Skeleton width="60%" height={24} radius="var(--radius-sm)" style={{ marginBottom: '0.5rem' }} />
        <Skeleton width="40%" height={16} radius="var(--radius-sm)" />
      </div>
    );
  }

  const prs = profile.stats || {};

  return (
    <div className={styles.page}>
      {/* Header card */}
      <Card style={{ marginBottom: '1.25rem' }}>
        <div className={styles.headerInner}>
          <div className={styles.avatarBlock}>
            <Avatar
              src={profile.avatarUrl}
              initials={profile.displayName?.charAt(0)?.toUpperCase() || 'A'}
              size="lg"
              style={{ marginBottom: '0.5rem' }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className={styles.avatarInput}
              aria-label="Upload profile photo"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarUploading}
            >
              {avatarUploading ? 'Uploading…' : 'Change photo'}
            </Button>
          </div>

          <div className={styles.infoBlock}>
            {editing ? (
              <div className={styles.form}>
                <Input
                  label="DISPLAY NAME"
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  placeholder="Display name"
                />
                <Label as="span" style={{ display: 'block', marginBottom: '0.25rem' }}>NATIONALITY</Label>
                <SearchableSelect
                  options={NATIONALITIES}
                  value={form.nationality}
                  onChange={(v) => setForm((f) => ({ ...f, nationality: v }))}
                  placeholder="Select nationality"
                />
                <NumericInput
                  label="AGE"
                  min="1"
                  max="120"
                  value={form.age}
                  onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
                />
                <Label as="span" style={{ display: 'block', marginBottom: '0.25rem' }}>DISCIPLINE</Label>
                <select
                  value={form.discipline}
                  onChange={(e) => setForm((f) => ({ ...f, discipline: e.target.value }))}
                  className={styles.nativeSelect}
                >
                  <option value="">Select</option>
                  {DISCIPLINES.map((d) => (
                    <option key={d} value={d}>{DISCIPLINE_LABELS[d]}</option>
                  ))}
                </select>
                <Textarea
                  label="BIO"
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  placeholder="Short bio"
                  rows={3}
                />
                <div className={styles.formActions}>
                  <Button variant="primary" size="lg" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Display as="h1" size="l" style={{ marginBottom: '0.2rem' }}>
                  {profile.displayName || 'Athlete'}
                </Display>
                {profile.bio && (
                  <Body tone="secondary" style={{ marginBottom: '0.4rem' }}>{profile.bio}</Body>
                )}
                {(() => {
                  const parts = [];
                  if (profile.nationality) parts.push(profile.nationality);
                  if (profile.age) parts.push(`${profile.age} yrs`);
                  if (profile.bodyweight != null) parts.push(`${profile.bodyweight} kg`);
                  if (profile.discipline) parts.push(DISCIPLINE_LABELS[profile.discipline] || profile.discipline);
                  return parts.length > 0 ? (
                    <Body tone="secondary" style={{ marginBottom: '0.75rem' }}>{parts.join(' · ')}</Body>
                  ) : null;
                })()}
                <div className={styles.headerActions}>
                  <Button variant="secondary" onClick={() => setEditing(true)}>Edit profile</Button>
                  <Button variant="ghost" as={Link} to="/compare/select">Compare with athlete</Button>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Notifications opt-in */}
      <PushOptInCard userId={userId} />

      {/* Referrals section */}
      <section className={styles.section}>
        <Title as="h2" style={{ marginBottom: '0.5rem' }}>Invite athletes</Title>
        <Body tone="secondary" style={{ marginBottom: '0.75rem' }}>
          Share your link. When someone you invite logs their first PR, you both get Pro free for 30 days.
        </Body>
        {(() => {
          const slug = (profile.displayName || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
          if (!slug) {
            return <Body tone="tertiary" style={{ fontSize: 13 }}>Set a display name to enable your invite link.</Body>;
          }
          const link = `https://barchain-24c66.web.app/signup?ref=${slug}`;
          const shareIt = async () => {
            const text = 'Join me on BarChain — verified strength leaderboard.';
            if (navigator.share) {
              try { await navigator.share({ title: 'BarChain', text, url: link }); return; }
              catch { /* fall through to copy */ }
            }
            try { await navigator.clipboard.writeText(link); }
            catch { /* no-op */ }
            // eslint-disable-next-line no-alert
            alert('Invite link copied to clipboard.');
          };
          return (
            <Card padding="md" frame="gold">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Label>YOUR INVITE LINK</Label>
                <code style={{ fontFamily: 'var(--font-numeric)', fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-all' }}>{link}</code>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <Button variant="primary" size="sm" onClick={shareIt}>Share invite</Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(link); }
                      catch { /* no-op */ }
                      // eslint-disable-next-line no-alert
                      alert('Invite link copied to clipboard.');
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </Card>
          );
        })()}
      </section>

      {/* PRs section */}
      <section className={styles.section}>
        <Title as="h2" style={{ marginBottom: '0.75rem' }}>PRs</Title>
        {(prs.squatPr != null || prs.benchPr != null || prs.deadliftPr != null) ? (
          <div className={styles.statsRow}>
            {prs.squatPr != null && <Stat label="Squat" value={`${prs.squatPr} kg`} />}
            {prs.benchPr != null && <Stat label="Bench" value={`${prs.benchPr} kg`} />}
            {prs.deadliftPr != null && <Stat label="Deadlift" value={`${prs.deadliftPr} kg`} />}
          </div>
        ) : (
          <Body tone="secondary">No PRs yet.</Body>
        )}
      </section>

      {/* Progress chart */}
      {workouts.length > 0 && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <Title as="h2" style={{ marginBottom: '0.75rem' }}>Progress (weight vs date)</Title>
          <select
            className={styles.nativeSelect}
            style={{ maxWidth: 220, marginBottom: '0.75rem' }}
            value={chartExercise}
            onChange={(e) => setChartExercise(e.target.value)}
          >
            <option value="">Select exercise</option>
            {exerciseOptions.map((ex) => (
              <option key={ex} value={ex}>{ex}</option>
            ))}
          </select>
          <WeightChart data={chartData} />
        </Card>
      )}

      {/* Lift history */}
      <section className={styles.section}>
        <Title as="h2" style={{ marginBottom: '0.75rem' }}>Lift history</Title>
        {workouts.length === 0 ? (
          <EmptyState
            icon="barbell"
            title="No lifts yet"
            body="Add one from the Add tab."
          />
        ) : (
          <ul className={styles.workoutList}>
            {workouts.slice(0, 50).map((w) => (
              <li key={w.id} className={`${styles.workoutItem} ${w.showInFeed ? styles.inFeed : ''}`}>
                <button
                  type="button"
                  className={styles.workoutThumbBtn}
                  onClick={() => w.videoUrl && setVideoModal({ videoUrl: w.videoUrl, exercise: w.exercise, weight: w.weight, reps: w.reps })}
                  disabled={!w.videoUrl}
                  aria-label={w.videoUrl ? 'Watch video' : 'No video'}
                >
                  <div className={styles.workoutThumb}>
                    {w.videoUrl ? (
                      <video src={w.videoUrl} className={styles.workoutVideo} muted playsInline />
                    ) : (
                      <div className={styles.workoutPlaceholder}>📋</div>
                    )}
                  </div>
                </button>
                <div
                  className={styles.workoutMeta}
                  role="button"
                  tabIndex={0}
                  onClick={() => w.videoUrl && setVideoModal({ videoUrl: w.videoUrl, exercise: w.exercise, weight: w.weight, reps: w.reps })}
                  onKeyDown={(e) => e.key === 'Enter' && w.videoUrl && setVideoModal({ videoUrl: w.videoUrl, exercise: w.exercise, weight: w.weight, reps: w.reps })}
                >
                  <Body as="span">{w.exercise} · {w.weight} kg × {w.reps}</Body>
                  <Body tone="secondary" as="div" style={{ fontSize: 13 }}>
                    {w.createdAt && (w.createdAt.toDate ? w.createdAt.toDate() : new Date(w.createdAt)).toLocaleDateString()}
                  </Body>
                </div>
                <div className={styles.workoutActions} onClick={(e) => e.stopPropagation()}>
                  {w.showInFeed ? (
                    <span className={styles.inFeedBadge}>In feed</span>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={settingFeedId !== null}
                      onClick={() => handleShowInFeed(w.id)}
                    >
                      {settingFeedId === w.id ? 'Setting…' : 'Show in feed'}
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={deletingId !== null}
                    onClick={() => handleDelete(w.id)}
                  >
                    {deletingId === w.id ? 'Deleting…' : 'Delete'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Video modal */}
      {videoModal && (
        <div
          className={styles.videoModalOverlay}
          onClick={() => setVideoModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Watch lift video"
        >
          <div className={styles.videoModal} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.videoModalClose}
              onClick={() => setVideoModal(null)}
              aria-label="Close"
            >
              ×
            </button>
            <video
              src={videoModal.videoUrl}
              className={styles.videoModalVideo}
              controls
              autoPlay
              playsInline
            />
            <Body style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border-hairline)' }}>
              {videoModal.exercise} · {videoModal.weight} kg × {videoModal.reps}
            </Body>
          </div>
        </div>
      )}
    </div>
  );
}

function PushOptInCard({ userId }) {
  const supported = canAskPushPermission();
  const [perm, setPerm] = useState(currentPushPermission());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  if (!supported) {
    return null;
  }
  if (perm === 'granted') {
    return null; // hidden once enabled
  }

  async function handleEnable() {
    if (!userId) return;
    setBusy(true);
    setMessage('');
    try {
      const res = await enablePushForUser(userId);
      setPerm(res.status === 'granted' ? 'granted' : res.status);
      if (res.status === 'granted') {
        setMessage('You will be notified when your coach leaves a note.');
      } else if (res.status === 'denied') {
        setMessage('Notifications are blocked. Enable them in your browser settings, then refresh.');
      } else if (res.status === 'unsupported') {
        setMessage('Your browser does not support push notifications.');
      }
    } catch (err) {
      setMessage(err?.message || 'Could not enable notifications.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.section}>
      <Title as="h2" style={{ marginBottom: '0.5rem' }}>Notifications</Title>
      <Card padding="md">
        <Body tone="secondary" style={{ marginBottom: '0.75rem' }}>
          Get pinged when your coach posts feedback on a lift or a new challenge launches.
        </Body>
        <Button variant="primary" size="sm" onClick={handleEnable} disabled={busy}>
          {busy ? 'Enabling…' : 'Enable notifications'}
        </Button>
        {message && (
          <Body tone="secondary" style={{ marginTop: '0.5rem', fontSize: 13 }}>{message}</Body>
        )}
      </Card>
    </section>
  );
}
